import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { FairUseReconciliationResponse } from "@/api/admin/fair-use-reconciliation"

import {
  FAIR_USE_RECONCILIATION_DAYS,
  getFairUseReconciliation,
  runFairUseReconciliation,
} from "@/api/admin/fair-use-reconciliation"

import { GC_TIME, MEDIUM_STALE_TIME } from "./_shared"
import { FAIR_USE_QUERY_KEY } from "./fair-use"

/**
 * The measurement-coverage window.
 *
 * Derived from `FAIR_USE_QUERY_KEY` rather than added to `lib/query-keys.ts`:
 * the prefix is what every fair-use invalidation already matches, and the
 * window length is the only thing that distinguishes two of these reads.
 */
export const FAIR_USE_RECONCILIATION_QUERY_KEY = [...FAIR_USE_QUERY_KEY, "reconciliation"] as const

export const fairUseReconciliationQueryKey = (days: number) =>
  [...FAIR_USE_RECONCILIATION_QUERY_KEY, days] as const

/** The last `days` nights of ledger-vs-Azure comparison. */
export function useFairUseReconciliationQuery(days = FAIR_USE_RECONCILIATION_DAYS, enabled = true) {
  return useQuery<FairUseReconciliationResponse>({
    queryKey: fairUseReconciliationQueryKey(days),
    queryFn: () => getFairUseReconciliation({ days }),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * Run the measurement for one day (platform admins only).
 *
 * The POST answers 202 the moment the run is SCHEDULED — the document does not
 * exist yet, so invalidating immediately would only re-read the same window.
 * The card tells the admin the run has started and leaves the refresh to them;
 * the invalidation here is what makes their next visit read fresh.
 */
export function useRunFairUseReconciliationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: { day?: string } = {}) => runFairUseReconciliation(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAIR_USE_RECONCILIATION_QUERY_KEY })
    },
  })
}
